import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface QuantityEditModalProps {
  isOpen: boolean;
  itemName: string;
  quantity: number;
  onClose: () => void;
  onSave: (newQuantity: number) => void;
}

export const QuantityEditModal: React.FC<QuantityEditModalProps> = ({
  isOpen,
  itemName,
  quantity,
  onClose,
  onSave,
}) => {
  const [value, setValue] = useState(quantity);
  const [initialQuantity, setInitialQuantity] = useState(quantity);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(quantity);
    setInitialQuantity(quantity);
  }, [quantity, isOpen]);

  if (!isOpen) return null;

  const handleIncrement = () => setValue(v => v + 1);
  const handleDecrement = () => setValue(v => Math.max(-1, v - 1));

  const handleSave = () => {
    if (value !== initialQuantity) {
      onSave(value);
      onClose();
    }
  };

  const hasChanged = value !== initialQuantity;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-bg-primary)',
          borderRadius: '16px',
          padding: '20px',
          minWidth: '280px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
        }}
      >
        {/* Header */}
        <div style={{
          fontSize: '15px',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
          marginBottom: '16px',
          textAlign: 'center',
          paddingBottom: '12px',
          borderBottom: '1px solid var(--color-border)',
        }}>
          {itemName}
        </div>

        {/* Quantity controls */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          marginBottom: '20px',
        }}>
          {/* Decrement button */}
          <button
            onClick={handleDecrement}
            disabled={value <= -1}
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              border: '2px solid var(--color-border-strong)',
              background: 'var(--color-bg-primary)',
              cursor: value <= -1 ? 'not-allowed' : 'pointer',
              fontSize: '24px',
              fontWeight: 300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: value <= -1 ? 'var(--color-border-strong)' : 'var(--color-text-secondary)',
              transition: 'all 0.15s ease',
            }}
          >
            −
          </button>

          {/* Input field */}
          <input
            ref={inputRef}
            type="number"
            value={value}
            onChange={(e) => setValue(Math.max(-1, parseInt(e.target.value) || 0))}
            onKeyDown={handleKeyDown}
            style={{
              width: '80px',
              height: '48px',
              textAlign: 'center',
              fontSize: '24px',
              fontWeight: 600,
              border: '2px solid var(--color-border)',
              borderRadius: '12px',
              outline: 'none',
            }}
          />

          {/* Increment button */}
          <button
            onClick={handleIncrement}
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              border: '2px solid var(--color-border-strong)',
              background: 'var(--color-bg-primary)',
              cursor: 'pointer',
              fontSize: '24px',
              fontWeight: 300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-text-secondary)',
              transition: 'all 0.15s ease',
            }}
          >
            +
          </button>
        </div>

        {/* Action buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
        }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '10px',
              border: '1px solid var(--color-border-strong)',
              background: 'var(--color-bg-primary)',
              color: 'var(--color-text-secondary)',
              fontSize: '15px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanged}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '10px',
              border: 'none',
              background: hasChanged ? 'var(--color-accent-secondary)' : 'var(--color-border-strong)',
              color: 'var(--color-text-inverse)',
              fontSize: '15px',
              fontWeight: 600,
              cursor: hasChanged ? 'pointer' : 'not-allowed',
              opacity: hasChanged ? 1 : 0.6,
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
